import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const dateSortComparator = (v1, v2) => {
  if (!v1 && !v2) return 0;
  if (!v1) return -1;
  if (!v2) return 1;
  return new Date(v1) - new Date(v2);
};

export const RAW_DATA_HEADER = [
  {
    field: "specimen_capture_date",
    headerName: "specimen_collection_date",
    description: "The date when the specimen was collected in the field",
    minWidth: 200,
    sortComparator: dateSortComparator,
    valueFormatter: (value) => (value ? dayjs.utc(value).format("M/D/YYYY") : ""),
  },
  {
    field: "collection_site",
    headerName: "collection_site",
    description:
      "User defined name for the collection site where the specimen was captured",
    minWidth: 200,
  },
  {
    field: "collection_method_display_name",
    headerName: "collection_method",
    description:
      "User defined name for the method used to capture tthe specimen",
    minWidth: 200,
  },
  {
    field: "summary_species",
    headerName: "final_result_id",
    description:
      "A reconciled species identification, where contributed_data_genus and contributed_data_species overwrite algorithmSpecies",
    minWidth: 200,
  },
  {
    field: "algorithmSpecies",
    headerName: "final_idx_id",
    description:
      "The calculated species identification by algorithm data, considering the algConfidenceThreshold and region. If a species is outside the region or if its confidence is below the algConfidenceThreshold, it flagged as unknown.",
    minWidth: 200,
  },
  {
    field: "unknownNotes",
    headerName: "unknownNotes",
    description:
      'The reason for "algorithmSpecies" being an unknown species (e.g., low confidence or outside the region).',
    minWidth: 200,
  },
  {
    field: "algorithm_genus_front",
    headerName: "idx_genus_front",
    description:
      "The genus identified by the algorithm from the front-view image of the specimen.",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "algorithm_species_front",
    headerName: "idx_species_front",
    description:
      "The species identified by the algorithm from the front-view image of the specimen.",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "algorithm_sex_front",
    headerName: "idx_sex_front",
    description:
      "The sex (male/female) identified by the algorithm from the front-view image of the specimen.",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "algorithm_empty_front",
    headerName: "idx_empty_front",
    description:
      "An indicator from the algorithm that the front-view image was empty, dirty, or a specimen was not detected.",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "algorithm_confidence_front",
    headerName: "idx_confidence_front",
    description:
      "The confidence score of the algorithm's identification for the front view.",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "algorithm_genus_back",
    headerName: "idx_genus_back",
    description:
      "The genus identified by the algorithm from the back-view image of the specimen.",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "algorithm_species_back",
    headerName: "idx_species_back",
    description:
      "The species identified by the algorithm from the back-view image of the specimen.",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "algorithm_sex_back",
    headerName: "idx_sex_back",
    description:
      "The sex (male/female) identified by the algorithm from the back-view image of the specimen.",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "algorithm_empty_back",
    headerName: "idx_empty_back",
    description:
      "An indicator from the algorithm that the back-view image was empty, dirty, or a specimen was not detected.",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "algorithm_confidence_back",
    headerName: "idx_confidence_back",
    description:
      "The confidence score of the algorithm's identification for the back view.",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "fused_algorithm_genus",
    headerName: "idx_genus",
    description:
      "The final genus identification determined by the algorithm after processing both front and back images",
    minWidth: 200,
  },
  {
    field: "fused_algorithm_species",
    headerName: "idx_species",
    description:
      "The final species identification determined by the algorithm after processing both front and back images",
    minWidth: 200,
  },
  {
    field: "fused_algorithm_sex",
    headerName: "idx_sex",
    description:
      "The final sex identification determined by the algorithm after processing both front and back images",
    minWidth: 200,
  },
  {
    field: "fused_algorithm_confidence",
    headerName: "idx_confidence",
    description:
      "The confidence score associated with the fused algorithmic identification after processing both front and back image data",
    minWidth: 200,
  },
  {
    field: "algVectorType",
    headerName: "vector_type",
    description:
      'The type of vector (“mosquito" "tick”) that the algorithm used at the time of capture',
    minWidth: 200,
  },
  {
    field: "algRegionName",
    headerName: "regional_algorithm",
    description:
      "The regional algorithm the device was set to at the time of capture. The region restricts which species the algorithm can return - anything outside it is flagged as out of region.",
    minWidth: 200,
  },
  {
    field: "algVersion",
    headerName: "algorithm_version",
    description:
      "The version number of the algorithm used at the time of capture",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "algConfidenceThreshold",
    headerName: "confidence_threshold",
    description:
      "The confidence threshold stored for the algorithm at the time of capture, below which identifications are considered unknown. Different algorithm versions have different predefined algorithm confidence thresholds",
    minWidth: 200,
    defaultHide: true,
  },
  {
    field: "tray",
    headerName: "tray_id",
    description: "A unique, auto increment identifier for the tray",
    minWidth: 200,
  },
  {
    field: "specimen",
    headerName: "specimen_id",
    description: "A unique identifier for the individual specimen",
    minWidth: 200,
  },
  {
    field: "institution_id",
    headerName: "organization_specimen_id",
    description:
      "A user-inputted identifier for the individual specimen within an organization",
    minWidth: 200,
  },
  {
    field: "user",
    headerName: "user",
    description: "The user account of the person who captured the tray",
    minWidth: 200,
  },
  {
    field: "position",
    headerName: "tray_position",
    description:
      "The physical position of the specimen within a tray ranging from 0 - 11",
    minWidth: 200,
  },
  {
    field: "device",
    headerName: "device_id",
    description: "IDX device name",
    minWidth: 200,
  },
  {
    field: "imaging_date_time",
    headerName: "imaging_date_time",
    description: "The precise date and time when the specimen was imaged.",
    minWidth: 200,
    sortComparator: dateSortComparator,
  },
  {
    field: "contributed_data_genus",
    headerName: "contributed_data_genus",
    description: "The genus as identified by the user",
    minWidth: 200,
  },
  {
    field: "contributed_data_species",
    headerName: "contributed_data_species",
    description: "The species as identified by the user",
    minWidth: 200,
  },
  {
    field: "contributed_data_sex",
    headerName: "contributed_data_sex",
    description: "The sex as identified by the user",
    minWidth: 200,
  },
  {
    field: "contributed_data_life_stage",
    headerName: "contributed_data_life_stage",
    description: "The life stage of the specimen as identified by user",
    minWidth: 200,
  },
  {
    field: "contributed_data_gonotrophic",
    headerName: "contributed_data_gonotrophic",
    description:
      "The gonotrophic state of the female mosquito as identified by the user",
    minWidth: 200,
  },
  {
    field: "contributed_data_plate",
    headerName: "microrplate_id",
    description:
      "Identifier for the microplate on which the specimen was stored or processed for future molecular testing",
    minWidth: 200,
  },
  {
    field: "contributed_data_well",
    headerName: "microplate_well_id",
    description:
      "Identifier for the specific well within a plate where the specimen was placed",
    minWidth: 200,
  },
  {
    field: "notes",
    headerName: "user_tray_notes",
    description: "Free-text notes associated with the tray by the user",
    minWidth: 200,
  },
  {
    field: "tags",
    headerName: "user_tray_tags",
    description: "Tags applied to the tray by the user",
    minWidth: 200,
  },
  {
    field: "specimen_tags",
    headerName: "user_specimen_tags",
    description: "Tags applied to the specimen by the user",
    minWidth: 200,
  },
  {
    field: "collection_method_vector_type",
    headerName: "collection_method_vector_type",
    description:
      'The type of vector targeted or associated with the collection method (“Mosquito" “Tick”).',
    minWidth: 200,
  },
  {
    field: "collection_method_trap_type",
    headerName: "collection_method_trap_type",
    description:
      'The style of trap used by the collection method (e.g., "BG Sentinel," "Gravid").',
    minWidth: 200,
  },
  {
    field: "collection_method_attractant_type",
    headerName: "collection_method_attractant_type",
    description:
      'The type of attractant associated with the collection method (e.g., "CO2," "Light,").',
    minWidth: 200,
  },
  {
    field: "collection_method_description",
    headerName: "collection_method_description",
    description: "A more detailed description of the collection method",
    minWidth: 200,
  },
  {
    field: "collection_site_address",
    headerName: "collection_site_address",
    description: "The address associated with the collection site",
    minWidth: 200,
  },
  {
    field: "collection_site_latitude",
    headerName: "collection_site_latitude",
    description: "The latitude coordinate associated with the collection site",
    minWidth: 200,
  },
  {
    field: "collection_site_longitude",
    headerName: "collection_site_longitude",
    description: "The longitude coordinate associated with the collection site",
    minWidth: 200,
  },
  {
    field: "collection_site_description",
    headerName: "collection_site_description",
    description: "A more detailed description of the collection site",
    minWidth: 200,
  },
];
